import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, GitPullRequest } from "lucide-react";
import api from "@/lib/api";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : Array.isArray(v) ? (v.length ? (typeof v[0] === "object" ? `${v.length} entr${v.length === 1 ? "y" : "ies"}` : v.join(", ")) : "—") : String(v));

const CR_TONES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  info_requested: "bg-orange-50 text-orange-700 border-orange-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  denied: "bg-red-50 text-red-700 border-red-200",
  superseded: "bg-slate-50 text-slate-400 border-slate-200",
};

export function CrStatusBadge({ status, label }) {
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${CR_TONES[status] || CR_TONES.superseded}`}>{label || status}</span>;
}

export function ChangeRequestCard({ cr, onDecide, showMember = false }) {
  const [reason, setReason] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = ["pending", "info_requested"].includes(cr.status);

  const decide = async (action) => {
    if (["deny", "request_info"].includes(action) && !reason.trim()) {
      setPendingAction(action);
      toast.error("A reason is required — enter it below, then confirm.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/network-command/change-requests/${cr.request_id}/decide`, { action, reason: reason || undefined });
      toast.success(`Change request ${action === "approve" ? "approved — profile updated" : action === "deny" ? "denied" : "sent back for more information"}`);
      setReason(""); setPendingAction(null);
      onDecide?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-100 p-4" data-testid={`nc-cr-${cr.request_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {showMember && <div className="text-sm font-semibold text-navy">{cr.member_name || cr.member_id}</div>}
          <div className="text-xs text-slate-400 font-mono">{cr.request_id} · requested {fmt(cr.created_at)} by {cr.requested_by || "provider"}</div>
        </div>
        <CrStatusBadge status={cr.status} label={cr.status_label} />
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="py-1.5 pr-3">Field</th><th className="py-1.5 pr-3">Current Value</th><th className="py-1.5">Requested Value</th>
          </tr></thead>
          <tbody>
            {(cr.changes || []).map((c, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="py-2 pr-3 font-medium text-navy">{c.label}</td>
                <td className="py-2 pr-3 text-slate-500">{val(c.current)}</td>
                <td className="py-2 font-semibold text-cobalt">{val(c.requested)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cr.info_request && (
        <div className="mt-2 text-xs text-orange-700 bg-orange-50 rounded-xl px-3 py-2">Info requested by {cr.info_request.by}: {cr.info_request.text}</div>
      )}
      {cr.decision_reason && !open && (
        <div className="mt-2 text-xs text-slate-500">Decision by {cr.decided_by} · {fmt(cr.decided_at)}: {cr.decision_reason}</div>
      )}
      {open && onDecide && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button onClick={() => decide("approve")} disabled={busy} data-testid={`nc-cr-approve-${cr.request_id}`}
            className="px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50">Approve & Apply</button>
          <button onClick={() => decide("deny")} disabled={busy} data-testid={`nc-cr-deny-${cr.request_id}`}
            className="px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-50">Deny</button>
          <button onClick={() => decide("request_info")} disabled={busy} data-testid={`nc-cr-info-${cr.request_id}`}
            className="px-3 py-1.5 rounded-full bg-cobalt hover:bg-cobalt/90 text-white text-xs font-semibold disabled:opacity-50">Request More Info</button>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`nc-cr-reason-${cr.request_id}`}
            placeholder={pendingAction ? "Reason (required) — then click again" : "Reason (required for deny / request info)"}
            className={`flex-1 min-w-[200px] px-3 py-1.5 rounded-xl border text-xs ${pendingAction ? "border-red-300 ring-2 ring-red-100" : "border-slate-200"}`} />
        </div>
      )}
    </div>
  );
}

export default function ChangeRequestsPanel({ network }) {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = () => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get(`/network-command/${network}/change-requests`, { params }).then(({ data }) => setData(data)).catch(() => setData({ requests: [] }));
  };
  useEffect(() => { load(); }, [network, statusFilter]);

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="nc-change-requests-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2">
          <GitPullRequest className="w-5 h-5 text-cobalt" /> Change Requests
          <span className="text-xs font-normal text-slate-400">— protected-field edits requiring administrative approval before going live</span>
        </h3>
        <div className="flex gap-1.5">
          {[["", "All"], ["pending", "Pending"], ["info_requested", "Needs Info"], ["approved", "Approved"], ["denied", "Denied"]].map(([k, l]) => (
            <button key={k} onClick={() => setStatusFilter(k)} data-testid={`nc-cr-filter-${k || "all"}`}
              className={`px-3 py-1 rounded-full text-xs font-medium ${statusFilter === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {data.requests.length === 0 && <div className="py-10 text-center text-sm text-slate-400" data-testid="nc-cr-empty">No change requests in this view.</div>}
        {data.requests.map((cr) => <ChangeRequestCard key={cr.request_id} cr={cr} onDecide={load} showMember />)}
      </div>
    </div>
  );
}
